import { apiRequest } from '@/lib/api'
import { apiRequest as clientApiRequest } from '@/lib/api/client'
import type { Institution, InstitutionAuthResponse, InstitutionMeResponse } from '@/types'

export interface RegisterInstitutionInput {
  name: string
  email: string
  password: string
}

export interface LoginInstitutionInput {
  email: string
  password: string
}

type GenericAuthResponse = {
  success?: boolean
  data?: {
    token?: string
    requires_two_factor?: boolean
    challenge_token?: string
  } & Record<string, unknown>
}

export interface VerifyTwoFactorLoginInput {
  challenge_token: string
  code: string
}

export type AddressInput = {
  postal_code?: string
  state?: string
  city?: string
  street?: string
  neighborhood?: string
  number?: string
  complement?: string
}

export type AdminProfileUpdateInput = {
  name?: string
  corporate_name?: string
  document_cnpj?: string
  email?: string
  phone?: string
  address?: AddressInput
}

export type InstitutionProfileUpdateInput = {
  person_type?: 'pf' | 'pj'
  name?: string
  corporate_name?: string
  document_cnpj?: string
  first_name?: string
  last_name?: string
  document_cpf?: string
  email?: string
  phone?: string
  gender?: string
  birth_date?: string
  address?: AddressInput
  profile_photo?: File | null
}

function appendFormValue(formData: FormData, key: string, value: string | File | null | undefined) {
  if (value === undefined || value === null || value === '') return
  formData.append(key, value)
}

export function buildInstitutionProfileFormData(
  input: InstitutionProfileUpdateInput
): FormData {
  const formData = new FormData()
  formData.append('_method', 'PUT')

  appendFormValue(formData, 'person_type', input.person_type)
  appendFormValue(formData, 'name', input.name)
  appendFormValue(formData, 'corporate_name', input.corporate_name)
  appendFormValue(formData, 'document_cnpj', input.document_cnpj)
  appendFormValue(formData, 'first_name', input.first_name)
  appendFormValue(formData, 'last_name', input.last_name)
  appendFormValue(formData, 'document_cpf', input.document_cpf)
  appendFormValue(formData, 'email', input.email)
  appendFormValue(formData, 'phone', input.phone)
  appendFormValue(formData, 'gender', input.gender)
  appendFormValue(formData, 'birth_date', input.birth_date)

  if (input.address) {
    formData.append('address', JSON.stringify(input.address))
  }

  if (input.profile_photo) {
    formData.append('profile_photo', input.profile_photo)
  }

  return formData
}

export async function registerInstitution(
  input: RegisterInstitutionInput
): Promise<InstitutionAuthResponse> {
  return apiRequest<InstitutionAuthResponse>('/api/institution/register', {
    method: 'POST',
    body: input,
  })
}

export async function loginInstitution(
  input: LoginInstitutionInput
): Promise<InstitutionAuthResponse> {
  return apiRequest<InstitutionAuthResponse>('/api/institution/login', {
    method: 'POST',
    body: input,
  })
}

export async function loginAdmin(
  input: LoginInstitutionInput
): Promise<GenericAuthResponse> {
  return apiRequest<GenericAuthResponse>('/api/admin/login', {
    method: 'POST',
    body: input,
  })
}

export async function verifyInstitutionTwoFactorLogin(
  input: VerifyTwoFactorLoginInput
): Promise<InstitutionAuthResponse> {
  return apiRequest<InstitutionAuthResponse>('/api/institution/login/2fa', {
    method: 'POST',
    body: input,
  })
}

export async function verifyAdminTwoFactorLogin(
  input: VerifyTwoFactorLoginInput
): Promise<GenericAuthResponse> {
  return apiRequest<GenericAuthResponse>('/api/admin/login/2fa', {
    method: 'POST',
    body: input,
  })
}

export async function loginDonor(
  input: LoginInstitutionInput
): Promise<GenericAuthResponse> {
  return apiRequest<GenericAuthResponse>('/api/donor/login', {
    method: 'POST',
    body: input,
  })
}

export async function fetchInstitutionMe(): Promise<InstitutionMeResponse> {
  return apiRequest<InstitutionMeResponse>('/api/institution/me', {
    method: 'GET',
  })
}

export async function fetchAdminMe(): Promise<unknown> {
  return apiRequest<unknown>('/api/admin/me', {
    method: 'GET',
  })
}

export async function fetchDonorMe(): Promise<unknown> {
  return apiRequest<unknown>('/api/donor/me', {
    method: 'GET',
  })
}

export async function logoutInstitution(): Promise<void> {
  await clientApiRequest('/api/institution/logout', { method: 'POST' })
}

export async function logoutAdmin(): Promise<void> {
  await clientApiRequest('/api/admin/logout', { method: 'POST' })
}

export async function logoutDonor(): Promise<void> {
  await clientApiRequest('/api/donor/logout', { method: 'POST' })
}

export async function updateAdminProfile(
  input: AdminProfileUpdateInput
): Promise<unknown> {
  return apiRequest<unknown>('/api/admin/profile', {
    method: 'PUT',
    body: input,
  })
}

export async function updateInstitutionProfile(
  input: InstitutionProfileUpdateInput
): Promise<unknown> {
  return clientApiRequest<unknown>('/api/institution/profile', {
    method: 'POST',
    body: buildInstitutionProfileFormData(input),
  })
}

export function extractInstitutionFromMe(
  response: InstitutionMeResponse
): Institution | null {
  if (!response?.success) return null
  return response.data?.institution ?? null
}
